import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MoreHorizontal, Download, Loader2 } from "lucide-react";
import { useExportPersonalizationCSV } from "../api/orderHooks";

const SOURCE_OPTIONS = [
  { value: "Storefront", label: "Storefront" },
  { value: "Shopee", label: "Shopee" },
  { value: "Lazada", label: "Lazada" },
] as const;

const STATUS_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
] as const;

const STORE_OPTIONS = [
  { value: "subscrub", label: "Subscrub" },
  { value: "0j85q3282r", label: "CSide" },
] as const;

/**
 * ⋯ menu with export actions.
 * Used on both the Orders list page and individual Order detail page.
 *
 * List view (no salesOrder): opens date range dialog before export.
 * Detail view (salesOrder set): exports single order directly.
 */
export function PersonalizationExportMenu({
  salesOrder,
  selectedOrders,
  onExport,
}: {
  salesOrder?: string;
  selectedOrders?: string[];
  /** When provided, adds a generic "Export" item that opens the ExportDialog */
  onExport?: () => void;
}) {
  const { exportCSV, isExporting } = useExportPersonalizationCSV();
  const hasSelection = selectedOrders && selectedOrders.length > 0;

  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);

  const handlePersonalizationClick = () => {
    if (salesOrder) {
      exportCSV(salesOrder);
    } else {
      setDateFrom("");
      setDateTo("");
      setSelectedSources([]);
      setSelectedStatuses([]);
      setSelectedStores([]);
      setDateDialogOpen(true);
    }
  };

  const toggleSource = (value: string) => {
    setSelectedSources((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleStatus = (value: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleStore = (value: string) => {
    setSelectedStores((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleExportWithFilters = () => {
    exportCSV(
      undefined,
      hasSelection ? selectedOrders : undefined,
      dateFrom || undefined,
      dateTo || undefined,
      selectedSources.length ? selectedSources : undefined,
      selectedStatuses.length ? selectedStatuses : undefined,
      selectedStores.length ? selectedStores : undefined,
    );
    setDateDialogOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="shrink-0">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onExport && (
            <DropdownMenuItem onClick={onExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={handlePersonalizationClick}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Personalization CSV
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export Personalization CSV</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Filter orders by date range, source, and payment status.
              Leave all empty to export all orders with personalization data.
            </p>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From</Label>
                <DatePicker
                  value={dateFrom}
                  onChange={setDateFrom}
                  placeholder="Start date"
                  className="w-full justify-start text-left text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>To</Label>
                <DatePicker
                  value={dateTo}
                  onChange={setDateTo}
                  placeholder="End date"
                  className="w-full justify-start text-left text-sm"
                />
              </div>
            </div>

            {/* Source Channel */}
            <div className="space-y-2">
              <Label>Source</Label>
              <div className="flex flex-wrap gap-4">
                {SOURCE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedSources.includes(opt.value)}
                      onCheckedChange={() => toggleSource(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Payment Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex flex-wrap gap-4">
                {STATUS_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedStatuses.includes(opt.value)}
                      onCheckedChange={() => toggleStatus(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Store / Brand */}
            <div className="space-y-2">
              <Label>Store</Label>
              <div className="flex flex-wrap gap-4">
                {STORE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedStores.includes(opt.value)}
                      onCheckedChange={() => toggleStore(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleExportWithFilters} disabled={isExporting}>
              {isExporting && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
